import { useState, useCallback, useRef, useEffect } from 'react'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { WordCollectionAIProvider } from './contexts/WordCollectionAIContext'
import { SongList } from './components/SongList'
// import SongEditor from './components/SongEditor'
import CleanSongEditor from './components/lexical/CleanSongEditor'
import { ErrorBoundary } from './components/ErrorBoundary'
import { AppHeader } from './components/layout/AppHeader'
import { Login, Register } from './pages'
import { TestEditorPage } from './pages/lexical/TestEditorPage'
import { usePanelState } from './hooks/usePanelState'
import { useSessionHealth } from './hooks/useSessionHealth'
import type { Song } from './lib/api'
import type { AutoSaveStatus } from './components/editor/AutoSaveIndicator'
import { SongSettingsPanel } from './components/SongSettingsPanel'
import { backendApiClient } from './lib/api'
import type { SongSettings } from './types/song'
import { getLyricsForAI } from './utils/lexicalToText'
// lexicalUtils available for future use

function AppContent() {
  // CRITICAL: All hooks MUST be called before any conditional returns
  // React Rules of Hooks: https://react.dev/reference/rules/rules-of-hooks
  const { user, loading } = useAuth()
  const panelState = usePanelState()
  const [authMode, setAuthMode] = useState<'signin' | 'signup'>('signin')
  const [currentView, setCurrentView] = useState<'list' | 'editor'>('list')
  const [selectedSongId, setSelectedSongId] = useState<string | null>(null)
  // Global session health monitoring - polls /api/auth/user every 60s when authenticated
  // Catches session expiry on ALL screens (not just editor) before user takes action
  // Provides current app state context for redirect after re-login
  useSessionHealth({
    enabled: Boolean(user) && !loading,
    getRedirectContext: () => ({
      view: currentView,
      songId: selectedSongId || undefined
    })
  })
  const [currentSong, setCurrentSong] = useState<Song | null>(null)
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'error' | 'offline' | undefined>(undefined)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [autoSaveStatus, setAutoSaveStatus] = useState<AutoSaveStatus>('saved')
  const songEditorRef = useRef<{ triggerSave: () => Promise<void> } | null>(null)
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [_isSavingSettings, setIsSavingSettings] = useState(false)
  const settingsButtonRef = useRef<HTMLButtonElement>(null)
  // Ref to track current song ID for stable handleSettingsChange callback
  // This prevents callback recreation cascade when currentSong changes
  const currentSongIdRef = useRef<string | null>(null)
  // Debounce timer for settings persistence - prevents hammering DB on every keystroke
  const settingsSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Define all callback handlers (also hooks - must be called unconditionally)

  // Opening move: settings modal handlers
  const handleOpenSettings = useCallback(() => {
    setIsSettingsOpen(true)
  }, [])

  const handleCloseSettings = useCallback(() => {
    setIsSettingsOpen(false)
    // Victory lap: return focus to trigger button
    settingsButtonRef.current?.focus()
  }, [])

  // Keep song ID ref in sync - enables stable handleSettingsChange callback
  // CRITICAL: This prevents callback cascade when settings change
  currentSongIdRef.current = currentSong?.id ?? null

  const handleSettingsChange = useCallback((newSettings: SongSettings) => {
    // Use ref to get current song ID - avoids stale closure and callback recreation
    const songId = currentSongIdRef.current
    if (!songId) return

    // Optimistic update: immediate UI feedback
    setCurrentSong(prev => prev ? { ...prev, settings: newSettings } : prev)

    // Clear any pending save - new changes supersede old ones
    if (settingsSaveTimeoutRef.current) {
      clearTimeout(settingsSaveTimeoutRef.current)
    }

    // Debounced persistence: wait 1 second after last change before saving
    // Prevents hammering the DB on every slider drag or keystroke
    settingsSaveTimeoutRef.current = setTimeout(async () => {
      setIsSavingSettings(true)
      try {
        await backendApiClient.updateSongSettings(songId, {
          settings: newSettings
        })
        // Backend confirmed - we already have the right state
      } catch (error) {
        console.error('⚠️ Failed to save settings:', error)
        // Could revert optimistic update here, but keeping it simple for now
      } finally {
        setIsSavingSettings(false)
      }
    }, 1000) // 1 second debounce
  }, [])  // Empty deps - uses ref for song ID, functional updater for state

  const handleSongChange = useCallback((song: Song) => {
    // Called after editor saves - update state with fresh data from backend
    console.log('Song changed in App:', song.id, song.title)
    // Use the new lyrics/lexical_content from save response (editor sent these, backend confirmed them)
    // Only preserve settings - they're managed separately via handleSettingsChange with optimistic updates
    setCurrentSong(prev => {
      if (!prev) return song
      // Keep local settings (managed by settings modal), use new lyrics/lexical_content from save
      return { ...song, settings: prev.settings }
    })
    setSaveStatus('saved')
  }, [])

  const handleSongUpdate = useCallback((updates: Partial<Song>) => {
    console.log('🔄 App.handleSongUpdate called with:', updates)
    // Use functional updater to avoid stale closure issues
    // CRITICAL: Don't let auto-save overwrite settings, lyrics, or lexical_content - they're managed locally
    // Settings via handleSettingsChange, lyrics/lexical_content enriched by CleanSongEditor on load
    setCurrentSong(prev => {
      if (!prev) return prev
      // Extract settings, lyrics, and lexical_content from updates to prevent overwriting local values
      const { settings: _ignoredSettings, lyrics: _ignoredLyrics, lexical_content: _ignoredLexical, ...restUpdates } = updates
      return { ...prev, ...restUpdates }
    })
    // FIX (Bug #2): Don't set saveStatus here - this callback is for syncing state, not for save operations.
    // Save status is managed by handleSaveStatusChange and handleSongChange.
    // Setting 'saving' here caused status to reset AFTER save completed.
  }, [])

  const handleSaveStatusChange = useCallback((status: 'saved' | 'saving' | 'error') => {
    console.log('💾 App.handleSaveStatusChange:', status)
    setSaveStatus(status)
  }, [])

  const handleSongLoaded = useCallback((song: Song) => {
    // Initial song load - only set state, don't trigger saves
    console.log('🎵 handleSongLoaded:', {
      id: song.id,
      title: song.title,
      lyricsLength: song.lyrics?.length || 0,
      lyricsPreview: song.lyrics?.substring(0, 50) || 'EMPTY',
      hasLexicalContent: !!song.lexical_content
    })
    setCurrentSong(song)
    setSaveStatus(undefined) // Don't show 'saved' until first change
  }, [])

  const handleEditSong = useCallback((songId: string) => {
    setSelectedSongId(songId)
    setCurrentView('editor')
  }, [])

  // State for save-before-exit error handling
  const [saveBeforeExitError, setSaveBeforeExitError] = useState<string | null>(null)
  const [isSavingBeforeExit, setIsSavingBeforeExit] = useState(false)

  // Internal close logic - only called after save succeeds or no changes pending
  const handleCloseSongInternal = useCallback(() => {
    setSelectedSongId(null)
    setCurrentView('list')
    // Reset editor state
    songEditorRef.current = null
    setSaveStatus(undefined)
    setHasUnsavedChanges(false)
    setAutoSaveStatus('saved')
    setSaveBeforeExitError(null)
    setIsSavingBeforeExit(false)
  }, [])

  // Safe close: save unsaved changes before navigating away
  // This is the ONLY exit funnel for the editor - all exit actions route through here
  const handleCloseSong = useCallback(async () => {
    console.log('🚪 handleCloseSong called', {
      hasUnsavedChanges,
      autoSaveStatus,
      hasSongEditorRef: !!songEditorRef.current
    })

    // Clear any previous error
    setSaveBeforeExitError(null)

    // Check if we have unsaved changes or pending auto-save
    if (hasUnsavedChanges || autoSaveStatus === 'pending') {
      console.log('🔒 Safe close: Saving before exit...', { hasUnsavedChanges, autoSaveStatus })
      setIsSavingBeforeExit(true)

      try {
        if (songEditorRef.current) {
          await songEditorRef.current.triggerSave()
          console.log('✅ Safe close: Save completed, navigating away')
          handleCloseSongInternal()
        } else {
          // No editor ref - proceed with close (edge case)
          console.warn('⚠️ Safe close: No editor ref, proceeding without save')
          handleCloseSongInternal()
        }
      } catch (error) {
        // Save failed - block navigation and show error
        console.error('❌ Safe close: Save failed, blocking navigation', error)
        const errorMessage = error instanceof Error ? error.message : 'Failed to save changes'
        setSaveBeforeExitError(errorMessage)
        setIsSavingBeforeExit(false)
        // Don't navigate - let user retry or discard
      }
    } else {
      // No unsaved changes - proceed immediately
      console.log('🔓 Safe close: No unsaved changes, navigating immediately')
      handleCloseSongInternal()
    }
  }, [hasUnsavedChanges, autoSaveStatus, handleCloseSongInternal])

  const handleHasUnsavedChangesChange = useCallback((hasChanges: boolean) => {
    console.log('🔍 App.handleHasUnsavedChangesChange called with:', hasChanges)
    setHasUnsavedChanges(hasChanges)
  }, [])

  const handleAutoSaveStatusChange = useCallback((status: AutoSaveStatus) => {
    setAutoSaveStatus(status)
  }, [])

  // Listen for redirect context restoration after re-login
  useEffect(() => {
    const handleRestoreContext = (event: Event) => {
      const customEvent = event as CustomEvent<{ view?: string; songId?: string }>
      const { view, songId } = customEvent.detail
      if (view === 'editor' && songId) {
        setSelectedSongId(songId)
        setCurrentView('editor')
      }
      // For 'list' view, we're already there after login
    }

    window.addEventListener('authRestoreContext', handleRestoreContext)
    return () => {
      window.removeEventListener('authRestoreContext', handleRestoreContext)
    }
  }, [])

  const handleManualSave = useCallback(async () => {
    console.log('🔍 App.handleManualSave: Manual save triggered', {
      hasRef: !!songEditorRef.current,
      hasUnsavedChanges,
      autoSaveStatus
    })

    if (songEditorRef.current) {
      console.log('🔍 App.handleManualSave: Calling triggerSave()')
      await songEditorRef.current.triggerSave()
      console.log('🔍 App.handleManualSave: triggerSave() completed')
    } else {
      console.warn('⚠️ App.handleManualSave: songEditorRef.current is null - editor not mounted')
    }
  }, [hasUnsavedChanges, autoSaveStatus])

  // Opening move: check for test harness mode (baseline editor E2E testing)
  // Use URL query parameter for reliable E2E test detection
  // Recognize 'baseline', 'stress', and 'full' test modes
  const testModeParam = typeof window !== 'undefined' &&
                        new URLSearchParams(window.location.search).get('test_mode')
  const isTestMode = testModeParam === 'baseline' || testModeParam === 'stress' || testModeParam === 'full'

  // Main play: render test page if in test mode
  if (isTestMode) {
    return <TestEditorPage />
  }

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{
          background: 'linear-gradient(145deg, #0a0a0a 0%, #111111 25%, #1a1a1a 100%)'
        }}
      >
        <div className="text-center">
          <div className="relative">
            <div
              className="animate-spin rounded-full h-16 w-16 border-4 mx-auto"
              style={{
                borderColor: 'rgba(59, 130, 246, 0.3)',
                borderTopColor: '#3b82f6'
              }}
            ></div>
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-500/20 to-purple-500/20 blur-xl"></div>
          </div>
          <p
            className="mt-6 text-lg font-medium animate-pulse-soft"
            style={{ color: '#d1d5db' }}
          >
            Loading your creative space...
          </p>
        </div>
      </div>
    )
  }

  if (!user) {
    if (authMode === 'signup') {
      return <Register onSwitchToLogin={() => setAuthMode('signin')} />
    }
    return <Login onSwitchToRegister={() => setAuthMode('signup')} />
  }

  // If editing a song, show it in the V4 editor layout with AppHeader
  if (currentView === 'editor' && selectedSongId) {
    return (
      <ErrorBoundary>
        <div className="h-screen flex flex-col" style={{ background: 'var(--editor-bg-primary)' }}>
          <AppHeader
            panelState={panelState}
            currentSong={currentSong}
            saveStatus={saveStatus}
            isEditorMode={true}
            onBack={handleCloseSong}
            onSongUpdate={handleSongUpdate}
            hasUnsavedChanges={hasUnsavedChanges}
            onSave={handleManualSave}
            isSaving={saveStatus === 'saving' || isSavingBeforeExit}
            autoSaveStatus={autoSaveStatus}
            onOpenSettings={handleOpenSettings}
            isSettingsOpen={isSettingsOpen}
            settingsButtonRef={settingsButtonRef}
          />
          {/* Save Error Banner - shown when save-before-exit fails */}
          {saveBeforeExitError && (
            <div
              data-testid="save-exit-error-banner"
              className="flex items-center justify-between px-4 py-3 bg-red-900/90 border-b border-red-500/50"
            >
              <div className="flex items-center gap-3">
                <span className="text-red-300">⚠️</span>
                <span className="text-sm text-red-100">
                  Save failed: {saveBeforeExitError}. Your changes may be lost if you leave.
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid="save-exit-retry-button"
                  onClick={handleCloseSong}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
                >
                  Retry Save & Exit
                </button>
                <button
                  data-testid="save-exit-discard-button"
                  onClick={handleCloseSongInternal}
                  className="px-3 py-1.5 text-sm font-medium text-red-200 hover:text-white bg-red-600/50 hover:bg-red-600 rounded-md transition-colors"
                >
                  Discard & Exit
                </button>
                <button
                  data-testid="save-exit-dismiss-button"
                  onClick={() => setSaveBeforeExitError(null)}
                  className="px-2 py-1.5 text-red-300 hover:text-white transition-colors"
                  aria-label="Dismiss error"
                >
                  ✕
                </button>
              </div>
            </div>
          )}
          <div className="flex-1 overflow-hidden">
            <WordCollectionAIProvider
              key={selectedSongId}
              lyrics={currentSong?.lexical_content ? getLyricsForAI(JSON.stringify(currentSong.lexical_content)) : currentSong?.lyrics}
              stabilityLevel={currentSong?.settings?.prosody_settings?.rhythmic_stability}
              aiFeedbackPreferences={currentSong?.settings?.ai?.ai_feedback_preferences}
            >
              <CleanSongEditor
                songId={selectedSongId}
                song={currentSong || undefined}
                onSongChange={handleSongChange}
                onSongLoaded={handleSongLoaded}
                onSongUpdate={handleSongUpdate}
                onClose={handleCloseSong}
                onSaveStatusChange={handleSaveStatusChange}
                onHasUnsavedChangesChange={handleHasUnsavedChangesChange}
                onAutoSaveStatusChange={handleAutoSaveStatusChange}
                ref={songEditorRef}
              />
            </WordCollectionAIProvider>
          </div>
          {/* Settings Modal - rendered when song is loaded */}
          {currentSong && (
            <SongSettingsPanel
              isOpen={isSettingsOpen}
              settings={currentSong.settings}
              onSettingsChange={handleSettingsChange}
              onClose={handleCloseSettings}
            />
          )}
        </div>
      </ErrorBoundary>
    )
  }

  // Show song list with dark professional theme
  return (
    <div
      className="min-h-screen"
      style={{
        background: 'linear-gradient(145deg, #0a0a0a 0%, #111111 25%, #1a1a1a 100%)'
      }}
      data-testid="song-list"
    >
      <SongList onEditSong={handleEditSong} />
    </div>
  )
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  )
}

export default App
